import React, { useEffect, useRef, useState } from 'react'
import { ConsentState } from '../../client/utils/onetrust-utils'
import { useMetaAdsConversionTags } from './ads'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'

interface FacebookPixelProps {
  consentState: ConsentState
  useLimitedDataUse?: boolean
}

export const FacebookPixel: React.FC<FacebookPixelProps> = ({
  consentState,
  useLimitedDataUse = false
}) => {
  const [isFbqReady, setIsFbqReady] = useState(!!window.fbq)
  const pageViewTracked = useRef(false)
  const initializedPixels = useRef<Set<string>>(new Set())
  const { data: metaData, isLoading: isMetaDataLoading } =
    useMetaAdsConversionTags()
  const {
    applicableConfigs: { pixelsConfig },
    isLoading: isRestaurantLoading
  } = useRestaurant()

  // Load Facebook Pixel script and track when it's ready
  useEffect(() => {
    if (!consentState.hasTargetingConsent || isFbqReady) return

    if (!window.fbq) {
      // Use Facebook's exact initialization code via script injection
      const script = document.createElement('script')
      script.innerHTML = `
        !function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ?
          n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s)
    }(window, document, 'script',
      'https://connect.facebook.net/en_US/fbevents.js');`
      document.head.appendChild(script)
    }

    // Wait for fbq to be available
    const maxAttempts = 100
    let attempts = 0
    const waitForFbq = setInterval(() => {
      attempts++
      const { fbq } = window

      if (fbq) {
        setIsFbqReady(true)
        clearInterval(waitForFbq)

        // Set Limited Data Use if needed
        if (useLimitedDataUse) {
          fbq('dataProcessingOptions', ['LDU'], 0, 0)
        }
      } else if (attempts >= maxAttempts) {
        console.warn(
          'Facebook Pixel: Stopped waiting for fbq after max attempts'
        )
        clearInterval(waitForFbq)
      }
    }, 100)

    return () => clearInterval(waitForFbq)
  }, [consentState.hasTargetingConsent, isFbqReady, useLimitedDataUse])

  // Handle consent changes after fbq is ready
  useEffect(() => {
    if (!isFbqReady) return

    if (consentState.hasTargetingConsent) {
      window.fbq('consent', 'grant')
    } else {
      window.fbq('consent', 'revoke')
    }
  }, [isFbqReady, consentState.hasTargetingConsent])

  // Initialize pixel from applicableConfigs
  useEffect(() => {
    if (
      !isFbqReady ||
      !consentState.hasTargetingConsent ||
      isRestaurantLoading ||
      !pixelsConfig?.facebook
    )
      return

    // Skip if already initialized
    if (initializedPixels.current.has(pixelsConfig.facebook)) return

    window.fbq('init', pixelsConfig.facebook)
    initializedPixels.current.add(pixelsConfig.facebook)
  }, [
    isFbqReady,
    consentState.hasTargetingConsent,
    isRestaurantLoading,
    pixelsConfig?.facebook
  ])

  // Initialize pixel from Meta Ads API (loads async)
  useEffect(() => {
    if (
      !isFbqReady ||
      !consentState.hasTargetingConsent ||
      isMetaDataLoading ||
      !metaData?.pixelId
    )
      return

    // Skip if already initialized
    if (initializedPixels.current.has(metaData.pixelId)) return

    window.fbq('init', metaData.pixelId)
    initializedPixels.current.add(metaData.pixelId)
  }, [
    isFbqReady,
    consentState.hasTargetingConsent,
    isMetaDataLoading,
    metaData?.pixelId
  ])

  // Track PageView once after all data sources are loaded and at least one pixel is initialized
  useEffect(() => {
    if (
      !isFbqReady ||
      !consentState.hasTargetingConsent ||
      isRestaurantLoading ||
      isMetaDataLoading ||
      initializedPixels.current.size === 0 ||
      pageViewTracked.current
    )
      return

    // Track page view once for all initialized pixels
    window.fbq('track', 'PageView')
    pageViewTracked.current = true
  }, [
    isFbqReady,
    consentState.hasTargetingConsent,
    isRestaurantLoading,
    isMetaDataLoading,
    // We check initializedPixels.current.size in the condition but can't use it as a dependency
    // Instead, we depend on the data that would cause pixels to be initialized
    pixelsConfig?.facebook,
    metaData?.pixelId
  ])

  return null
}
